/**
 * RELEASE HOLDS: features built and on staging that production does not get
 * yet. Talk to humans (Connect, games night, speaking rooms, calls) and Study
 * groups have been held since 2026-09-25 (Tom + Aran). Tom, 8 Oct, job #744:
 * "Keep social hidden - it's not really been tested enough yet."
 *
 * A hold has two halves.
 *
 * THE WEBSITE HALF is VITE_RELEASE_HOLDS_OFF. Production leaves it unset, so
 * the features are held there. Staging sets it to 1 on its own Vercel scope,
 * so staging shows them. To release a feature on the website, set
 * VITE_RELEASE_HOLDS_OFF=1 and RELEASE_HOLDS_OFF=1 on Production. The hold is
 * in code on purpose: every environment shares one Supabase, so a config row
 * would hide the feature on staging too.
 *
 * THE STORE APP HALF (plan 036, Apple 2.5.19). The store apps are a window onto
 * the website, so a website release would switch the feature on inside an app
 * whose review never saw it. Inside a native shell a hold lifts only when the
 * website has released it AND the app's own build names it in SHELL_ALLOWLIST
 * (packages/player-vue/capacitor.config.ts). An app built without the name
 * stays held for good, which is right: its review never saw the feature.
 * The order matters: a build names a feature only after its own origin's
 * website has released it, so review sees the feature. Otherwise these
 * variables would switch it on inside an app after review.
 *
 * NEVER DELETE THIS FILE TO RELEASE A FEATURE. That was once a way to release.
 * It would now delete the store app half too.
 *
 * The server twin is api/_utils/releaseHolds.ts. It has only the website half.
 */
import { shellHolds, type ShellFeature } from '@/platform/capabilities'

/**
 * Every hold reads this at call time. A production build leaves the variable
 * unset, so everything below is held. The unit-test configs set it to '1' so
 * the held features' own tests keep testing their code for next week; the
 * hold's tests (releaseHolds.test.ts) switch it back off.
 */
const holdsOff = (): boolean => import.meta.env.VITE_RELEASE_HOLDS_OFF === '1'

/**
 * The name a store build gives each hold in its SHELL_ALLOWLIST. A new hold
 * needs a name here and in SHELL_FEATURES; RELEASE_HOLDS is typed from these
 * keys, so a getter without a name does not compile.
 */
export const HOLD_APP_FEATURE = {
  talkToHumans: 'talk-to-humans',
  studyGroups: 'study-groups',
} as const satisfies Record<string, ShellFeature>

type Hold = keyof typeof HOLD_APP_FEATURE

/** Held on the website, or held in a store app whose build does not name it. */
function held(hold: Hold): boolean {
  if (!holdsOff()) return true
  return shellHolds(HOLD_APP_FEATURE[hold])
}

export const RELEASE_HOLDS: Readonly<Record<Hold, boolean>> = {
  /** /talk, /connect (and calls), /games, /rooms, the speaking-room chip. */
  get talkToHumans(): boolean { return held('talkToHumans') },
  /** /groups, /groups/:id, /groups/invite/:token. */
  get studyGroups(): boolean { return held('studyGroups') },
}

const TALK_PREFIXES = ['/talk', '/connect', '/games', '/rooms']
const GROUP_PREFIXES = ['/groups']

// Vue Router matches case-insensitively, so /TALK must be held too.
const under = (path: string, prefixes: string[]) => {
  const lower = path.toLowerCase()
  return prefixes.some((p) => lower === p || lower.startsWith(`${p}/`))
}

/** Is this route a held feature's page? */
export function isHeldPath(path: string): boolean {
  return (RELEASE_HOLDS.talkToHumans && under(path, TALK_PREFIXES))
    || (RELEASE_HOLDS.studyGroups && under(path, GROUP_PREFIXES))
}

/** Both social rows held: nothing needs to ask the server about the social door. */
export const socialHeld = (): boolean => RELEASE_HOLDS.talkToHumans && RELEASE_HOLDS.studyGroups
